import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as offersActions from '@store/offers/offers.actions';

import { OffersAPI } from '../../../data/mo-api/offers.api';
import { map, switchMap, catchError, tap, withLatestFrom, delay } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Offers, PreviewOffer } from '../../../core/models/offers.model';
import { MapErrorService } from '../../services/error/map-error.service';
import { ErrorFacade } from '../../../facades/error.facade';
import { OffersService } from '../../services/offers.service';
import { Store } from '@ngrx/store';
import * as offersSelectors from '@store/offers/offers.selectors';
import { OffersFacade } from '../../../facades/offers.facade';
import { UtilService } from '../../../services/util.service';
import { APP_ROUTES as ROUTES } from '@app/app.routes';
import * as appSelectors from '@store/app/app.selectors';
import { HandleTokenService } from '../../../core/services/handle-token.service';
import { AppFacade } from '../../../facades/app.facade';
import { IExtraData, IPutRecord } from '../../../core/models/app.model';
import { EVENTS } from '@app/data/events/events-by-category';
import { StorageService } from '@app/data/browser/storage.service';

@Injectable()
export class OffersEffects {
  constructor(
    private actions$: Actions,
    private _store: Store,
    private _offersAPI: OffersAPI,
    private _appFacade: AppFacade,
    private _errorFacade: ErrorFacade,
    private _offersFacade: OffersFacade,
    private _mapErrorService: MapErrorService,
    private _offersService: OffersService,
    private _utilService: UtilService,
    private _handleTokenService: HandleTokenService,
    private _storageService: StorageService
  ) {}

  public getOffersByMerchant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offersActions.getOffersByMerchant),
      delay(500),
      withLatestFrom(this._store.select(offersSelectors.selectAmount)),
      switchMap(([_, amount]) => {
        const event = EVENTS.REDIRECT;
        this._appFacade.sendPutRecord(event);
        const isMobile = window.innerWidth < 991;
        return this._offersAPI.getOffersByMerchant(amount).pipe(
          map((offers: Offers[]) => {
            const sortOrder = isMobile ? [12, 6, 3] : [3, 12, 6];
            const sortOffers: Offers[] = [...offers].sort((a, b) => {
              const indexA = sortOrder.indexOf(a.repayment_frequency.minimum_installments);
              const indexB = sortOrder.indexOf(b.repayment_frequency.minimum_installments);
              if (indexA === -1 || indexB === -1) {
                return (
                  a.repayment_frequency.minimum_installments -
                  b.repayment_frequency.minimum_installments
                );
              }
              return indexA - indexB;
            });
            return offersActions.successGetOffersByMerchant({ offers: sortOffers });
          }),
          catchError((error) => of(offersActions.failureGetOffersByMerchant({ message: error })))
        );
      })
    )
  );

  public successGetOffersByMerchant$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(offersActions.successGetOffersByMerchant),
        withLatestFrom(this._store.select(offersSelectors.selectAmount)),
        tap(([{ offers }, amount]) => {
          const offersRequest = this._offersService.setOffersLoopById(offers, amount);
          const route: string = ROUTES.OFFERS;
          const event: IPutRecord = EVENTS.OFFER.VISIT;
          // NEW ONE
          this._appFacade.sendPutRecord(event);
          return (
            !!amount && offers.length > 0
              ? this._offersFacade.getOfferPreview(offersRequest)
              : this._utilService.navigateTo(route),
            this._offersFacade.setOfferLoading()
          );
        })
      ),
    { dispatch: false }
  );

  public failureGetOffersByMerchant$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(offersActions.failureGetOffersByMerchant),
        tap((_) => {
          const route: string = ROUTES.ERROR;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public getOfferPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offersActions.getOfferPreview),
      switchMap(({ offersRequest }) => {
        return forkJoin(offersRequest).pipe(
          map((request) => offersActions.successGetOfferPreview({ previewOffers: request })),
          catchError((error) => of(offersActions.failureGetOfferPreview({ message: error })))
        );
      })
    )
  );

  public successGetOfferPreview$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(offersActions.successGetOfferPreview),
        tap(({ previewOffers }) => {
          const isMobile = window.innerWidth < 991;
          const middleIndex = isMobile ? 0 : Math.floor(previewOffers.length / 2);
          const firstPreviewOffer: PreviewOffer = [...previewOffers][middleIndex];
          this._offersFacade.setPreviewOffer(firstPreviewOffer);
          const route: string = ROUTES.OFFERS;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public failureGetOfferPreview$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(offersActions.failureGetOfferPreview),
        tap(({ message }) => {
          const messageError = this._mapErrorService.mapError(message);
          const route: string = ROUTES.ERROR;
          this._errorFacade.handleError(messageError);
          this._offersFacade.setOfferAmount('');
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  private _handleOfferForEvent(type: number): Object {
    const typeToString = type.toString();
    const event = EVENTS.OFFER.SELECT_OFFER.OFFER_TYPE;
    return { ...event, label: event.label.replace('type', typeToString) };
  }

  public setSelectedOfferId$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(offersActions.setSelectedOfferId),
        withLatestFrom(this._store.select(offersSelectors.selectNumberOfInstallments)),
        tap(([{ offerId }, numberOfInstallments]) => {
          this._storageService.setItem('offerId', offerId);
          this._storageService.setItem('numberOfInstallments', numberOfInstallments);
          const offerSelected = this._storageService.getItem('offerSelected');
          const previewOffer = this._storageService.getItem('previewOffer');
          const data = this._handleOfferForEvent(offerSelected);
          this._appFacade.sendPutRecord(data);
          const event = EVENTS.OFFER.EVENT;
          const extraData: IExtraData = {
            request: { offerId },
            response: { offerSelected, previewOffer },
          };
          this._appFacade.sendPutRecord(event, extraData);
          const route: string = ROUTES.AUTH;
          this._utilService.navigateTo(route);
        })
      ),
    { dispatch: false }
  );

  public getOneOfferPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(offersActions.getOneOfferPreview),
      withLatestFrom(
        this._store.select(appSelectors.selectTransactions),
        this._store.select(offersSelectors.selectSelectedOfferId),
        this._store.select(offersSelectors.selectNumberOfInstallments)
      ),

      switchMap(([_, { amount }, offer_id, numberOfInstallments]) => {
        const transactionToken: string = this._handleTokenService.getTransactionToken();
        return this._offersAPI
          .getOfferPreview(offer_id, amount, numberOfInstallments, transactionToken)
          .pipe(
            map((previewOffer) => {
              return offersActions.successOneOfferPreview({ previewOffer });
            }),
            catchError((error) => of(offersActions.failureOneOfferPreview({ message: error })))
          );
      })
    )
  );
}
